import { ProtectedComponent } from '@components'

import { Button, Icon, Typography, XStack, YStack } from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { Check, Landmark, MousePointerClick } from 'lucide-react'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankIntegrationContextProvider } from '@features/openBanking/contexts/bankIntegrationContext'
import { MastercardTrustNote } from '@features/openBanking/elements/MastercardTrustNote'
import type { UiSourceValue } from '@features/reconciliation/enums'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { BankReconciliationConnectBank } from '../../../../assets/images'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { getImageClassName } from '../../../../utils/getClassName'
import { useOpenConnectBankModal } from '../../hooks/useOpenConnectBankModal'
import * as Styled from './styles'

interface BankingOnboardingEmptyStateProps {
  /** Where the empty state is shown, sent along when the user starts a bank connection */
  uiSource: UiSourceValue
  onTryDemo: () => void
}

const BULLET_KEYS = [
  'bank.onboarding_empty_state.bullet_one',
  'bank.onboarding_empty_state.bullet_two',
  'bank.onboarding_empty_state.bullet_three',
]

export const BankingOnboardingEmptyState = ({
  uiSource,
  onTryDemo,
}: BankingOnboardingEmptyStateProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const openConnectBankModal = useOpenConnectBankModal()

  const handleConnectBankClick = useCallback(() => {
    openConnectBankModal(uiSource)
  }, [openConnectBankModal, uiSource])

  const handleTryDemo = useCallback(() => {
    track('XXX - Reconciliation Transactions Demo Opened (Client)')
    onTryDemo()
  }, [onTryDemo, track])

  return (
    // Provides the connect-bank modal for views that don't mount it themselves (e.g. Transactions)
    <BankIntegrationContextProvider organizationId={organization?.id ?? ''}>
      <YStack align="center" gap={32} justify="center" paddingHorizontal={16} paddingVertical={40}>
        <Styled.Illustration className={getImageClassName()}>
          <BankReconciliationConnectBank />
        </Styled.Illustration>
        <YStack align="center" gap={8}>
          <Typography.Header as="h3">{t('bank.onboarding_empty_state.title')}</Typography.Header>
          <YStack align="center" gap={8} role="list">
            {BULLET_KEYS.map((key) => (
              <XStack key={key} align="center" gap={8} justify="center" role="listitem">
                <Icon color={colors.content.neutral.medium} icon={Check} size="small" />
                <Typography.Text color={colors.content.neutral.medium} size="medium">
                  {t(key)}
                </Typography.Text>
              </XStack>
            ))}
          </YStack>
        </YStack>
        <XStack align="center" gap={12} justify="center" wrap="wrap">
          <ProtectedComponent scopes={Scope.BankSyncWrite}>
            <Button
              label={t('bank.onboarding_empty_state.connect')}
              onClick={handleConnectBankClick}
              size="large"
              startIcon={<Icon icon={Landmark} />}
            />
          </ProtectedComponent>
          <Button
            label={t('bank.onboarding_empty_state.try_demo')}
            onClick={handleTryDemo}
            size="large"
            startIcon={<Icon icon={MousePointerClick} />}
            variant="outlined"
          />
        </XStack>
        <MastercardTrustNote />
      </YStack>
    </BankIntegrationContextProvider>
  )
}
